/**
 * Der Spielzustand im Browser — **gespiegelt, nicht gerechnet.**
 *
 * Der Server ist autoritativ (`CLAUDE.md` Regel 1); diese Schicht nimmt seine Nachrichten
 * entgegen und baut daraus das, was die Oberfläche zeigt. Sie entscheidet nichts über das Spiel
 * und erfindet nichts — deshalb ist sie eine **pure Funktion** über Nachricht und bisherigem
 * Stand, und deshalb braucht sie keinen Socket, um geprüft zu werden. Dieselbe Trennung wie in
 * der Engine: was rechnet, hat keine Verbindung nach außen.
 *
 * **Ein `STATE` für jemand anderen wird verworfen.** Er sollte nie ankommen — die Projektion
 * schickt jedem seinen eigenen (§45). Käme doch einer, wäre ihn zu übernehmen der Bruch der
 * geheimen Aufstellung, und zwar genau in der Phase, in der es zählt. Fail closed: eine
 * Nachricht, die nicht für mich ist, ist keine Nachricht.
 *
 * **Der Zustand wird für die Engine aufgebaut, nicht als flache Form gehalten.** Die Oberfläche
 * rechnet legale Aktionen mit `@flaghex/engine` (**E26**); jede Komponente den Zustand selbst
 * aufbauen zu lassen wäre dieselbe Arbeit an vielen Stellen — und eine davon vergäße die
 * Prüfung.
 *
 * **Ein Fehler verschwindet nicht von selbst.** Wer eine Absicht geschickt hat und abgewiesen
 * wurde, soll es lesen können; die nächste Uhr-Nachricht kommt nach einer Sekunde. Weggeräumt
 * wird er durch eine Handlung (`dismissError`), nicht durch die Zeit.
 */

import { deserializeGameState, type GameEvent, type LobbyView, type ServerMessage, type WsErrorCode } from '@flaghex/contracts';
import type { GameState } from '@flaghex/engine';

/** Die Uhren, wie der Server sie zuletzt gemeldet hat (§48). */
export interface ClockView {
  readonly remaining: Readonly<Record<string, number>>;
  readonly running: string | null;
  readonly at: number;
}

/** Der öffentliche Stand der Aufstellung (Nr. 35) — Zahlen, keine Koordinaten. */
export interface SetupView {
  readonly progress: readonly {
    readonly player: string;
    readonly placed: number;
    readonly total: number;
    readonly confirmed: boolean;
  }[];
  readonly deadlineAt: number | null;
}

export interface ErrorView {
  /** Auch `MALFORMED`: eine Antwort, die der Client nicht aufbauen konnte (siehe unten). */
  readonly code: WsErrorCode | 'MALFORMED';
  readonly message: string;
  readonly requestId?: string;
}

/** Die aufgelöste Preview — dieselbe Form wie `LegalAction` in der Engine. */
export type PreviewView = Extract<ServerMessage, { type: 'PREVIEW' }>['preview'];

export interface GameView {
  readonly gameId: string | undefined;
  readonly playerId: string | undefined;
  readonly state: GameState | undefined;
  readonly preview: PreviewView;
  readonly clock: ClockView | undefined;
  readonly connectedPlayers: readonly string[];
  readonly lobby: LobbyView | undefined;
  readonly setup: SetupView | undefined;
  readonly events: readonly GameEvent[];
  /**
   * Ist diese Partie **regelwidrig erzeugt** worden? (Nr. 38, **E39** — Zyklus 11g)
   *
   * Steht neben dem Zustand und nicht darin: die Engine kennt keine Herkunft. Daraus wird das
   * **Abzeichen am Statusbereich** — Nr. 38 verlangt, dass man eine erzwungene Stellung sieht
   * und nicht in der Historie suchen muss.
   */
  readonly ruleOverridden: boolean;
  readonly lastError: ErrorView | undefined;
}

export const EMPTY_VIEW: GameView = {
  gameId: undefined,
  playerId: undefined,
  state: undefined,
  preview: null,
  clock: undefined,
  connectedPlayers: [],
  lobby: undefined,
  setup: undefined,
  events: [],
  ruleOverridden: false,
  lastError: undefined,
};

/** Räumt eine Fehlermeldung weg — durch eine Handlung, nicht durch die Zeit. */
export function dismissError(view: GameView): GameView {
  return view.lastError === undefined ? view : { ...view, lastError: undefined };
}

/**
 * Wendet eine Servernachricht an.
 *
 * Gibt bei Nachrichten ohne Wirkung **denselben** Wert zurück (Identität, nicht Kopie) — so
 * zeichnet React nicht neu, wenn sich nichts geändert hat.
 */
export function applyMessage(view: GameView, message: ServerMessage): GameView {
  switch (message.type) {
    case 'WELCOME':
      // Eine **andere** Partie setzt alles zurück: wer einem zweiten Einladungslink folgt, darf
      // keine Figuren, Ereignisse oder Uhren der vorigen Partie mitnehmen. Dieselbe Partie ist
      // dagegen ein Reconnect (§46) — da bleibt, was schon da ist.
      return view.gameId === message.gameId
        ? { ...view, playerId: message.playerId }
        : { ...EMPTY_VIEW, gameId: message.gameId, playerId: message.playerId };

    case 'STATE': {
      if (message.viewer !== view.playerId) {
        // Siehe Modulkopf: eine Nachricht, die nicht für mich ist, ist keine Nachricht.
        return view;
      }
      try {
        return {
          ...view,
          state: deserializeGameState(message.state),
          ruleOverridden: message.ruleOverridden,
        };
      } catch {
        // Die Form stimmte (das Schema hat sie geprüft), die Engine lehnt sie trotzdem ab —
        // also ein Fehler des Servers. Den alten Zustand stehenzulassen ist die bessere
        // Antwort als ein leerer Bildschirm.
        return {
          ...view,
          lastError: {
            code: 'MALFORMED',
            message: 'Der Dienst hat einen Zustand geschickt, der sich nicht aufbauen lässt',
          },
        };
      }
    }

    case 'PREVIEW':
      return { ...view, preview: message.preview };

    case 'CLOCK':
      return {
        ...view,
        clock: { remaining: message.remaining, running: message.running, at: message.at },
      };

    case 'PRESENCE':
      return { ...view, connectedPlayers: message.connected };

    case 'LOBBY':
      return { ...view, lobby: message.lobby };

    case 'SETUP':
      return { ...view, setup: { progress: message.progress, deadlineAt: message.deadlineAt } };

    case 'EVENT':
      return { ...view, events: [...view.events, message.event] };

    // **Ersetzt, ergänzt nicht.** `HISTORY` ist die vollständige Liste (§67 Schritt 47) und
    // kommt beim Anmelden und beim Reconnect; anzuhängen würde nach jedem Reconnect jedes
    // Ereignis verdoppeln.
    case 'HISTORY':
      return { ...view, events: [...message.events] };

    case 'ERROR':
      return {
        ...view,
        lastError: {
          code: message.code,
          message: message.message,
          ...(message.requestId === undefined ? {} : { requestId: message.requestId }),
        },
      };
  }
}
